import { Button } from "@aix/ui"
import { useState } from "react"
import type { Coach } from "../../../types/coach-os.types"

interface DebugTabProps {
  coach: Coach
}

export default function DebugTab({ coach }: DebugTabProps) {
  const [collapsed, setCollapsed] = useState(false)

  const debugData = {
    coach,
    // Add more debug data here as features are implemented
    quarterlyPlans: "Not yet implemented",
    weeklyPlans: "Not yet implemented",
    trackables: "Not yet implemented",
    checkIns: "Not yet implemented",
    insights: "Not yet implemented",
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Debug Information</h2>
          <p className="text-muted-foreground">JSON output of coach data and related entities</p>
        </div>
        <Button variant="outline" onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? "Expand All" : "Collapse All"}
        </Button>
      </div>

      {/* Coach Data */}
      <div className="bg-secondary rounded-lg">
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Coach Data</h3>
        </div>
        {!collapsed && (
          <div className="p-4">
            <pre className="text-xs bg-background p-4 rounded-lg overflow-x-auto">{JSON.stringify(coach, null, 2)}</pre>
          </div>
        )}
      </div>

      {/* API Endpoints */}
      <div className="bg-secondary rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Available API Endpoints</h3>
        <div className="space-y-2 text-sm font-mono">
          <div className="bg-background p-3 rounded">
            <span className="text-green-600 font-semibold">✓</span> coaches.get
          </div>
          <div className="bg-background p-3 rounded">
            <span className="text-green-600 font-semibold">✓</span> coaches.update
          </div>
          <div className="bg-background p-3 rounded">
            <span className="text-green-600 font-semibold">✓</span> coaches.archive
          </div>
          <div className="bg-background p-3 rounded">
            <span className="text-green-600 font-semibold">✓</span> coaches.delete
          </div>
          <div className="bg-background p-3 rounded">
            <span className="text-green-600 font-semibold">✓</span> plans.quarterlyPlans.generate
          </div>
          <div className="bg-background p-3 rounded">
            <span className="text-green-600 font-semibold">✓</span> plans.weeklyPlans.generate
          </div>
          <div className="bg-background p-3 rounded">
            <span className="text-green-600 font-semibold">✓</span> coachChat.create
          </div>
          <div className="bg-background p-3 rounded">
            <span className="text-green-600 font-semibold">✓</span> coachChat.sendMessage
          </div>
          <div className="bg-background p-3 rounded">
            <span className="text-green-600 font-semibold">✓</span> coachChat.getContext
          </div>
        </div>
      </div>

      {/* Schema Info */}
      <div className="bg-secondary rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Database Schema</h3>
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2">coaches</h4>
            <div className="text-xs font-mono text-muted-foreground space-y-1">
              <p>• id (uuid, PK)</p>
              <p>• userId (uuid, FK → users)</p>
              <p>• name, icon, description</p>
              <p>• type (pillar | side-quest)</p>
              <p>• tone (tough-love | chill | science-y)</p>
              <p>• status (active | paused | completed | archived)</p>
              <p>• startDate, targetEndDate, actualEndDate</p>
              <p>• metadata (jsonb)</p>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2">quarterly_plans</h4>
            <div className="text-xs font-mono text-muted-foreground space-y-1">
              <p>• id (uuid, PK)</p>
              <p>• coachId (uuid, FK → coaches)</p>
              <p>• durationWeeks, startDate, endDate</p>
              <p>• phases (jsonb), milestones (jsonb)</p>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2">weekly_plans</h4>
            <div className="text-xs font-mono text-muted-foreground space-y-1">
              <p>• id (uuid, PK)</p>
              <p>• quarterlyPlanId (uuid, FK → quarterly_plans)</p>
              <p>• coachId (uuid, FK → coaches)</p>
              <p>• weekNumber, weekStartDate, weekEndDate</p>
              <p>• status (upcoming | active | completed | skipped)</p>
              <p>• expectations (jsonb), dailyStructure (jsonb)</p>
              <p>• completionCount, totalActions</p>
              <p>• adjusted, adjustmentReason, originalPlan</p>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2">trackables (extended)</h4>
            <div className="text-xs font-mono text-muted-foreground space-y-1">
              <p>• coachId (uuid, FK → coaches) - NEW</p>
              <p>• fieldKey (text) - NEW for extraction</p>
              <p>• isRequired (boolean) - NEW</p>
              <p>• displayOrder (integer) - for UI ordering</p>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2">coach_insights</h4>
            <div className="text-xs font-mono text-muted-foreground space-y-1">
              <p>• id (uuid, PK)</p>
              <p>• coachId (uuid, FK → coaches)</p>
              <p>• periodType (weekly | monthly | quarterly | ad-hoc)</p>
              <p>• insightType (pattern | win | blocker | suggestion | celebration)</p>
              <p>• title, description, confidence</p>
              <p>• supportingData (jsonb)</p>
              <p>• userFeedback (accepted | rejected | implemented | ignored)</p>
            </div>
          </div>
        </div>
      </div>

      {/* All Debug Data */}
      <div className="bg-secondary rounded-lg">
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Complete Debug Data</h3>
        </div>
        {!collapsed && (
          <div className="p-4">
            <pre className="text-xs bg-background p-4 rounded-lg overflow-x-auto">
              {JSON.stringify(debugData, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  )
}
